/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDBadge from "components/MDBadge";
import MDButton from "components/MDButton";

// Material Dashboard 2 React example components
import DashboardLayout from "ScreenLayouts/ContainerLayout/DashboardLayout";
import DashboardNavbar from "ScreenLayouts/NavbarLayout/DashboardNavbar";
import Footer from "ScreenFooters/DefaultFooter";
import BasicTable from "ScreenLayouts/TableLayout/BasicTableLayout/BasicTable";

// Components

import { useMaterialUIController } from "context";

// Network
import { request } from "app/network";
import { formatLocalDateTime } from "utils/dateFormatter";
import { Component } from "react";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";

interface RequestListState {
    requests: any[];
    pageSize: number;
    pageNumber: number;
    totalCount: number;
    loading: boolean;

}

const PAGE_SIZE_STORAGE_KEY = "mediinstep_gifticon_request_list_page_size";

class GifticonRequestHistoryScreenClass extends Component<any, RequestListState> {
    constructor(props: any) {
        super(props);
        const savedPageSize = localStorage.getItem(PAGE_SIZE_STORAGE_KEY);
        const initialPageSize = savedPageSize ? parseInt(savedPageSize, 10) : 10;

        this.state = {
            requests: [],
            pageSize: initialPageSize,
            pageNumber: 1,
            totalCount: 0,
            loading: false,

        };
    }

    componentDidMount() {
        this.loadRequests();
    }

    loadRequests = async () => {
        try {
            this.setState({ loading: true });

            const param = {
                PageNumber: this.state.pageNumber,
                PageSize: this.state.pageSize,
                UseType: "POINT_USE_GIFTISHOW" // Filter for Gifticon requests
            };

            const res = await request("PointUseRequest.pagedList", { paramz: param });

            if (res && res.data) {
                const pagedList = res.data;
                const requests = (pagedList.items || []).map((req: any) => {
                    const crtDt = req.crtDt
                        ? formatLocalDateTime(req.crtDt)
                        : "-";

                    const processedDt = req.processedDt
                        ? formatLocalDateTime(req.processedDt)
                        : "-";

                    // Extract giftishow info safely
                    const giftInfo = req.giftishowProduct || {};

                    return {
                        id: req.seqId,
                        seqId: req.seqId,
                        crtDt,
                        userName: req.userName || "-",
                        requestPoint: req.requestPoint?.toLocaleString() || "0",
                        requestStatus: req.requestStatus,
                        processedByName: req.processedByName || "-",
                        processedDt,
                        requestMessage: req.requestMessage || "-",
                        adminMemo: req.adminMemo || "-",
                        rejectionReason: req.rejectionReason || "-",
                        userPhone: req.userPhone || "-",
                        senderPhone: req.senderPhone || "-",
                        receiverPhone: req.receiverPhone || "-",
                        // Giftishow specific fields
                        brandName: giftInfo.brandName || "-",
                        goodsName: giftInfo.goodsName || "-",
                        goodsImgS: giftInfo.goodsImgS || "",
                        trId: req.trId || "-",
                        processedBy: req.processedBy || "-"
                    };
                });

                this.setState({
                    requests,
                    totalCount: pagedList.totalCount || 0,
                    loading: false,
                });
            } else {
                this.setState({ loading: false });
            }
        } catch (error: any) {
            console.error("기프티쇼 거래내역 조회 실패:", error);
            this.setState({ loading: false });
        }
    };

    handlePageChange = (pageIndex: number) => {
        this.setState({ pageNumber: pageIndex + 1 }, () => {
            this.loadRequests();
        });
    };

    handlePageSizeChange = (pageSize: number) => {
        localStorage.setItem(PAGE_SIZE_STORAGE_KEY, pageSize.toString());
        this.setState({ pageSize, pageNumber: 1 }, () => {
            this.loadRequests();
        });
    };

    handleSync = async () => {
        if (!window.confirm("전송됨(SENT) 상태의 주문들을 최신 상태로 동기화하시겠습니까?\n취소된 주문은 자동으로 포인트가 환불됩니다.")) {
            return;
        }

        try {
            this.setState({ loading: true });
            await request("Giftishow.SyncOrders");
            window.alert("동기화가 완료되었습니다.");
            this.loadRequests();
        } catch (error: any) {
            console.error("동기화 실패:", error);
            window.alert("동기화 중 오류가 발생했습니다: " + (error.message || "알 수 없는 오류"));
            this.setState({ loading: false });
        }
    };



    render() {
        const { requests, pageSize, loading, totalCount, pageNumber } = this.state;

        const renderDateCell = (value: string | null) => {
            if (!value) return "-";
            const date = new Date(value);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hour = String(date.getHours()).padStart(2, '0');
            const min = String(date.getMinutes()).padStart(2, '0');
            const sec = String(date.getSeconds()).padStart(2, '0');

            return (
                <MDBox lineHeight={1.2}>
                    <MDTypography variant="caption" display="block" color="text" fontWeight="medium">
                        {`${year}-${month}-${day}`}
                    </MDTypography>
                    <MDTypography variant="caption" display="block" color="text">
                        {`${hour}:${min}:${sec}`}
                    </MDTypography>
                </MDBox>
            );
        };

        const columns = [
            {
                Header: "요청일시",
                accessor: "crtDt",
                align: "center",
                width: "10%",
                Cell: ({ value }: any) => renderDateCell(value)
            },
            {
                Header: "처리일시",
                accessor: "processedDt",
                align: "center",
                width: "10%",
                Cell: ({ value }: any) => renderDateCell(value)
            },
            { Header: "사용자명", accessor: "userName", align: "center", width: "8%" },
            { Header: "거래 ID", accessor: "trId", align: "center", width: "12%" },
            { Header: "브랜드", accessor: "brandName", align: "center", width: "10%" },
            {
                Header: "상품명",
                accessor: "goodsName",
                align: "left",
                width: "15%",
                Cell: ({ row }: any) => (
                    <MDBox display="flex" alignItems="center">
                        {row.original.goodsImgS && (
                            <MDBox component="img" src={row.original.goodsImgS} width="30px" mr={1} borderRadius="4px" />
                        )}
                        <MDTypography variant="caption" fontWeight="medium">
                            {row.original.goodsName}
                        </MDTypography>
                    </MDBox>
                )
            },
            {
                Header: "차감 포인트",
                accessor: "requestPoint",
                align: "right",
                width: "8%",
                Cell: ({ row }: any) => (
                    <MDTypography variant="button" fontWeight="bold" color="dark">
                        {row.original.requestPoint} P
                    </MDTypography>
                )
            },
            { Header: "수신번호", accessor: "receiverPhone", align: "center", width: "10%" },
            {
                Header: "상태",
                accessor: "requestStatus",
                align: "center",
                width: "8%",
                Cell: ({ row }: any) => {
                    const status = row.original.requestStatus;
                    let badgeColor: "warning" | "success" | "error" | "info" | "secondary" | "primary" | "light" | "dark" = "warning";
                    let badgeText = "대기"; // Default PENDING

                    if (status === "APPROVED" || status === "POINT_REQ_APPROVED" || status === "SUCCESS") {
                        badgeColor = "success";
                        badgeText = "성공";
                    } else if (status === "REJECTED" || status === "POINT_REQ_REJECTED") {
                        badgeColor = "error";
                        badgeText = "거절됨";
                    } else if (status === "CANCELLED" || status === "POINT_REQ_CANCELED" || status === "POINT_REQ_CANCELLED") {
                        badgeColor = "secondary"; // Grey/Secondary for Cancelled
                        badgeText = "취소됨";
                    } else if (status === "FAILED") {
                        badgeColor = "error";
                        badgeText = "실패";
                    } else if (status === "PROCESSING") {
                        badgeColor = "info";
                        badgeText = "처리중";
                    } else if (status === "ERROR") {
                        badgeColor = "error";
                        badgeText = "오류";
                    } else if (status === "TIMEOUT") {
                        badgeColor = "error";
                        badgeText = "시간초과";
                    } else if (status === "SENT") {
                        badgeColor = "success";
                        badgeText = "전송됨";
                    } else if (status === "USED") {
                        badgeColor = "info"; // or dark/primary
                        badgeText = "사용됨";
                    }

                    return (
                        <MDBadge
                            badgeContent={badgeText}
                            color={badgeColor}
                            variant="gradient"
                            size="sm"
                        />
                    );
                }
            },

            { Header: "처리자", accessor: "processedBy", align: "center", width: "8%" },

        ];

        const rows = requests;

        const totalPages = Math.ceil(totalCount / pageSize);
        const pageCountArray = Array.from({ length: totalPages }, (_, i) => i);
        const currentPageIndex = pageNumber - 1;
        const pageTenNumber = Math.floor(currentPageIndex / 10) * 10;

        const paginationCriteria = {
            PageNumber: currentPageIndex,
            PageCount: pageCountArray,
            PageTenNumber: pageTenNumber,
            totalCount: totalCount,
        };

        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox pt={6} pb={3} position="relative">
                    <Card sx={{ "& table": { tableLayout: "fixed", width: "100%" } }}>
                        <MDBox p={3} lineHeight={1}>
                            <MDBox display="flex" justifyContent="space-between" alignItems="center">
                                <MDBox display={"flex"} alignContent="center">
                                    <Icon fontSize={"medium"}>card_giftcard</Icon>
                                    <MDTypography ml={1} variant="h5" fontWeight="medium">
                                        기프티쇼 거래내역
                                    </MDTypography>
                                </MDBox>
                                <MDBox display="flex" flexDirection="column" alignItems="flex-end">
                                    <MDButton
                                        variant="gradient"
                                        color="info"
                                        size="small"
                                        onClick={this.handleSync}
                                        disabled={loading}
                                    >
                                        <Icon>sync</Icon>&nbsp;주문 상태 동기화
                                    </MDButton>
                                    <MDTypography variant="caption" color="text" mt={0.5}>
                                        *매일 새벽 3시(KST) 자동 동기화됨
                                    </MDTypography>
                                </MDBox>
                            </MDBox>
                            <MDTypography variant="button" color="text" mt={1}>
                                기프티콘 구매 및 발송 신청 목록 (전체: {totalCount}개)
                            </MDTypography>
                        </MDBox>
                        <BasicTable
                            canSearch={false}
                            criteria={paginationCriteria}
                            storePageSize={pageSize}
                            goPage={this.handlePageChange}
                            setStorePageSize={this.handlePageSizeChange}
                            table={{ columns, rows }}
                            isSorted={false}
                            entriesPerPage={{
                                defaultValue: pageSize,
                                entries: [5, 10, 15, 20, 25, 30, 40, 50],
                            }}
                            showTotalEntries={true}
                            noEndBorder
                        />
                        {loading && (
                            <MDBox
                                position="absolute"
                                top={0}
                                left={0}
                                right={0}
                                bottom={0}
                                display="flex"
                                alignItems="center"
                                justifyContent="center"
                                bgcolor="rgba(0, 0, 0, 0.3)"
                                sx={{ pointerEvents: "none" }}
                            >
                                <MDBox display="flex" gap={1.5}>
                                    <CircularProgress size={40} sx={{ color: "#49a3f1" }} />
                                </MDBox>
                            </MDBox>
                        )}
                    </Card>
                </MDBox>
                <Footer />


            </DashboardLayout>
        );
    }
}

function GifticonRequestHistoryScreen(props: any) {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    return <GifticonRequestHistoryScreenClass {...props} darkMode={darkMode} />;
}

export default GifticonRequestHistoryScreen;
